import { styled } from '@linaria/react';
import { useIsExporting } from '../artifact-export-context';
import { formatCellValue } from './freeform-chart-artifact.helpers';
import type { FreeformChartData } from './freeform-chart-artifact.type';

/**
 * Renders the table freeform chart type: headers from `columns`, cells
 * verbatim, in a scroll box whose height cap lifts while exporting so every
 * row lands in the screenshot.
 *
 * @param props.payload - The validated columnar `{columns, rows}` data.
 * @returns The scrollable table.
 */
function FreeformTable({ payload }: { payload: FreeformChartData }) {
  const isExporting = useIsExporting();

  return (
    <TableScroll $isExporting={isExporting} data-exporting={isExporting || undefined}>
      <StyledTable>
        <thead>
          <tr>
            {payload.columns.map((column, colIdx) => (
              // Column names may repeat in freeform data; key by position.
              <Th key={colIdx}>{column}</Th>
            ))}
          </tr>
        </thead>
        <tbody>
          {payload.rows.map((row, rowIdx) => (
            // Rows and columns carry no stable identity; index keys are fine
            // for this static (never reordered) table.
            <Tr key={rowIdx} $striped={rowIdx % 2 === 1}>
              {payload.columns.map((_column, colIdx) => (
                <Td key={colIdx}>{formatCellValue(row[colIdx])}</Td>
              ))}
            </Tr>
          ))}
        </tbody>
      </StyledTable>
    </TableScroll>
  );
}

// The export path screenshots the #artifact-root element, and an element
// screenshot clips overflow rather than scrolling it — a capped box would
// drop every row past the cap. Let the box grow while exporting so the
// whole table lands in the image.
const TableScroll = styled.div<{ $isExporting: boolean }>`
  max-height: ${({ $isExporting }) => ($isExporting ? 'none' : '480px')};
  overflow: ${({ $isExporting }) => ($isExporting ? 'visible' : 'auto')};
  border: 1px solid var(--flora-table-border);
  border-radius: 8px;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 12px;
`;

// The shared header token is a translucent tint, which scrolled rows show
// through once the table is tall enough to scroll. Layer the tint over an
// opaque surface here rather than changing the token — measure-comparison
// shares it.
const Th = styled.th`
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: var(--base-white);
  background-image: linear-gradient(var(--flora-table-header-bg), var(--flora-table-header-bg));
  padding: 8px 12px;
  text-align: left;
  font-weight: 600;
  color: var(--ink-dark);
  border-bottom: 1px solid var(--flora-table-border);
  /* border-box so the 80px column floor includes padding: the BE render_chart
     tool caps tables at 11 columns on the assumption that 11 × 80px fits the
     1000px export frame — content-box columns would be 104px and break it */
  box-sizing: border-box;
  min-width: 80px;
  overflow-wrap: anywhere;
`;

const Tr = styled.tr<{ $striped: boolean }>`
  background: ${({ $striped }) => ($striped ? 'var(--flora-table-stripe-bg)' : 'transparent')};
`;

// overflow-wrap: an unbroken token (URL, hash, id) would otherwise stretch
// its column past the fixed-width export frame, and the element screenshot
// crops whatever hangs outside it.
const Td = styled.td`
  padding: 6px 12px;
  color: var(--ink-base);
  border-bottom: 1px solid var(--flora-table-border);
  overflow-wrap: anywhere;
`;

export { FreeformTable };
